// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Pure selectors that derive the operator-console tab views from one alarm set,
// mirroring the mock's renderVals() derivations (Alarms vs Anomalies split, tab
// counts, group-filter pills, and the Severity/Newest/Oldest sort).

import type { Alarm } from '../types/alarm';
import type { AlarmSort } from '../stores/alarmsViewStore';
import { SEVERITY } from '../theme/severity';

/** The five operator-console tabs. */
export type AlarmTabKey = 'alarms' | 'anomalies' | 'routine' | 'recommendations' | 'closed';

export interface AlarmTabDef {
  key: AlarmTabKey;
  label: string;
  /** Route the tab navigates to. */
  route: string;
  /** Chip color tokens { fg, bg }. */
  fg: string;
  bg: string;
}

/**
 * Tab bar definition, in display order. All five tabs route to /home/:bucket,
 * which HomeAlarms serves — so every tab renders with the same card components.
 */
export const ALARM_TABS: AlarmTabDef[] = [
  {
    key: 'alarms',
    label: 'Alarms',
    route: '/home/alarms',
    fg: 'var(--sev1fg)',
    bg: 'var(--sev1bg)',
  },
  {
    key: 'anomalies',
    label: 'Anomalies',
    route: '/home/anomalies',
    fg: 'var(--sev2fg)',
    bg: 'var(--sev2bg)',
  },
  {
    key: 'routine',
    label: 'Routine',
    route: '/home/routine',
    fg: 'var(--sev4fg)',
    bg: 'var(--sev4bg)',
  },
  {
    key: 'recommendations',
    label: 'Recommendations',
    route: '/home/recommendations',
    fg: 'var(--oppfg)',
    bg: 'var(--oppbg)',
  },
  {
    key: 'closed',
    label: 'Closed',
    route: '/home/closed',
    fg: 'var(--text-helper)',
    bg: 'var(--layer-02)',
  },
];

/** The identity columns a detail route carries in its path + query string. */
type DetailPathAlarm = Pick<Alarm, 'id' | 'siteId' | 'assetId' | 'alarmId'>;

/**
 * Shared builder for the alarm and anomaly detail routes — they take the same
 * identity, and upstream they are the same row shape.
 *
 * `alarm_code` (the path segment) alone doesn't identify a row: it is the RULE,
 * which every piece of equipment firing it shares, and AEM 409s on an ambiguous
 * code. So `alarm_id` — the per-row lifecycle identifier — rides along and makes
 * the lookup exact. `equipment` stays as a narrowing fallback for rows with no
 * `alarm_id`, and so links created before this still resolve. All three are
 * equality filters on the alarms/anomalies endpoints, so the URL maps straight
 * onto the lookup query.
 */
function detailPath(base: string, alarm: DetailPathAlarm): string {
  const params = new URLSearchParams();
  if (alarm.siteId) params.set('site', alarm.siteId);
  if (alarm.alarmId) params.set('alarm_id', alarm.alarmId);
  if (alarm.assetId) params.set('equipment', alarm.assetId);
  const qs = params.toString();
  const path = `${base}/${encodeURIComponent(alarm.id)}`;
  return qs ? `${path}?${qs}` : path;
}

/** Detail route for an anomaly. See {@link detailPath}. */
export function anomalyDetailPath(alarm: DetailPathAlarm): string {
  return detailPath('/anomalies', alarm);
}

/** Detail route for an alarm. See {@link detailPath}. */
export function alarmDetailPath(alarm: DetailPathAlarm): string {
  return detailPath('/alarms', alarm);
}

/** Map a /home/:bucket route param to the tab it selects. */
export function tabForBucket(bucket: string | undefined): AlarmTabKey {
  switch (bucket) {
    // `findings` and `high` are legacy aliases — kept so old bookmarks resolve.
    case 'anomalies':
    case 'findings':
    case 'high':
      return 'anomalies';
    case 'routine':
    case 'daily':
      return 'routine';
    case 'recommendations':
    case 'optimizations':
      return 'recommendations';
    case 'closed':
      return 'closed';
    // `critical` is the legacy alias for `alarms`.
    case 'alarms':
    case 'critical':
    case 'all':
    default:
      return 'alarms';
  }
}

const isAlarm = (a: Alarm) => (a.kind ?? 'alarm') === 'alarm';

/** Alarms belonging to a tab, for a given site. */
export function selectTabAlarms(alarms: Alarm[], tab: AlarmTabKey, siteId?: string): Alarm[] {
  const scope = alarms.filter(a => !siteId || a.siteId === siteId);
  switch (tab) {
    case 'alarms':
      return scope.filter(a => isAlarm(a) && !a.closed && a.validated);
    case 'anomalies':
      return scope.filter(a => isAlarm(a) && !a.closed && !a.validated);
    case 'routine':
      return scope.filter(a => a.kind === 'routine' && !a.closed);
    case 'recommendations':
      return scope.filter(a => a.kind === 'optwork' && !a.closed);
    case 'closed':
      // One mixed queue: every kind that has been closed, whatever it was.
      return scope.filter(a => a.closed);
    default:
      return [];
  }
}

/** Per-tab counts for the tab bar badges (null → no badge). */
export function selectTabCounts(
  alarms: Alarm[],
  siteId?: string
): Record<AlarmTabKey, number | null> {
  const n = (tab: AlarmTabKey) => selectTabAlarms(alarms, tab, siteId).length || null;
  return {
    alarms: n('alarms'),
    anomalies: n('anomalies'),
    routine: n('routine'),
    recommendations: n('recommendations'),
    closed: n('closed'),
  };
}

/** Distinct group labels present in a list, alphabetical. */
export function groupsOf(list: Alarm[]): string[] {
  const seen = new Set<string>();
  list.forEach(a => {
    if (a.group) seen.add(a.group);
  });
  return Array.from(seen).sort();
}

/** Filter a list to a group ('all' → unchanged). */
export function applyGroup(list: Alarm[], group: string): Alarm[] {
  return group === 'all' ? list : list.filter(a => a.group === group);
}

/** Rank for a severity that isn't in the SEVERITY map at all. Stays last. */
const UNRANKED_SEVERITY_ORDER = 99;

/**
 * Sort weight for the severity ordering; lower sorts first.
 *
 * Reads straight off the displayed severity, so the order always matches the
 * chip the operator sees. An alarm with no known priority maps to 'Unknown'
 * (order 5 — after P4), so it sinks without needing a separate rule here.
 */
function severityOrder(alarm: Alarm): number {
  return SEVERITY[alarm.sev]?.order ?? UNRANKED_SEVERITY_ORDER;
}

/** Sort a list by the active sort key. */
export function sortAlarms(list: Alarm[], sort: AlarmSort): Alarm[] {
  // ISO instants compare chronologically, so unlike the HH:MM label this replaced they cross midnight.
  const raisedAt = (alarm: Alarm) => alarm.eventTimestamp ?? '';
  const byTimeDesc = (x: Alarm, y: Alarm) => {
    const [a, b] = [raisedAt(x), raisedAt(y)];
    return a < b ? 1 : a > b ? -1 : 0;
  };
  const out = [...list];
  out.sort((x, y) => {
    if (sort === 'newest') return byTimeDesc(x, y);
    if (sort === 'oldest') return -byTimeDesc(x, y);
    return severityOrder(x) - severityOrder(y) || byTimeDesc(x, y);
  });
  return out;
}
